import {
  useBuildingsShortQuery,
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  useProvidersShortQuery,
  useRolesQuery,
  useTeamsShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { SelectChangeEvent } from '@mui/material';
import { useUserTableStore } from '../filters.store';
import { isEqual, omit } from 'lodash';

const statusOptions = [
  { name: 'Active', value: 'active' },
  { name: 'Deactivated', value: 'deactivated' },
  { name: 'Invitation pending', value: 'invitation_pending' },
  { name: 'Invitation expired', value: 'invitation_expired' },
  { name: 'Password reset', value: 'password_reset' },
  { name: 'Missing MFA', value: 'missing_mfa' },
];

const entityTypeOptions = [
  { name: 'Organization', value: 'organization' },
  { name: 'Partner', value: 'partner' },
  { name: 'Building', value: 'building' },
  { name: 'Provider', value: 'provider' },
];

export const useFiltersCard = () => {
  const {
    filters,
    draftFilters,
    setDraftFilters,
    handleToggleFiltersCard,
    resetFilters,
    applyDraftFilters,
    draftFiltersCount,
  } = useUserTableStore();

  const { data: rolesOptions = [] } = useRolesQuery();
  const { data: organizationsShortOptions = [] } = useOrganizationsShortQuery();
  const { data: partnersShortOptions = [] } = usePartnerShortQuery();
  const { data: buildingsShortOptions = [] } = useBuildingsShortQuery({});
  const { data: providersShortOptions = [] } = useProvidersShortQuery({});
  const { data: teamShortOptions = [] } = useTeamsShortQuery({});
  const { data: supervisorOptions = [] } = useUserListShortQuery({
    roles: ['organization_supervisor', 'partner_supervisor'],
  });

  const dynamicEntityNameOptions = () => {
    switch (draftFilters.assigned_entity_type) {
      case 'organization':
        return organizationsShortOptions;
      case 'partner':
        return partnersShortOptions;
      case 'building':
        return buildingsShortOptions;
      case 'provider':
        return providersShortOptions;
      default:
        return [];
    }
  };

  const handleChangeStatusFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ status: e.target.value, page: 1 });
  };

  const handleChangeRolesFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ role: e.target.value, page: 1 });
  };

  const handleChangeTeamFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({ team_id: e.target.value, page: 1 });
  };

  const handleChangeSupervisorFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({
      supervisor_id: e.target.value,
      page: 1,
    });
  };

  const handleChangeEntityTypeFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({
      assigned_entity_type: e.target.value,
      assigned_entity_id: '',
      page: 1,
    });
  };

  const handleChangeEntityNameFilter = (e: SelectChangeEvent<string>) => {
    setDraftFilters({
      assigned_entity_id: e.target.value,
      page: 1,
    });
  };

  const isResetDisabled = draftFiltersCount === 0;
  const isApplyDisabled = isEqual(
    omit(draftFilters, ['search', 'page', 'limit']),
    omit(filters, ['search', 'page', 'limit']),
  );

  return {
    filters: draftFilters,
    statusOptions: statusOptions.map(o => ({
      value: o.value,
      label: o.name,
    })),
    handleChangeStatusFilter,
    rolesOptions: rolesOptions.map(o => ({
      value: o.role_name,
      label: o.display_name,
    })),
    handleChangeRolesFilter,
    entityTypeOptions: entityTypeOptions.map(o => ({
      value: o.value,
      label: o.name,
    })),
    teamTypeOptions: teamShortOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    supervisorOptions: supervisorOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleChangeEntityTypeFilter,
    handleChangeTeamFilter,
    dynamicEntityNameOptions: dynamicEntityNameOptions().map(o => ({
      value: o.id,
      label: o.name,
    })),
    handleChangeEntityNameFilter,
    handleChangeSupervisorFilter,
    handleToggleFiltersCard,
    resetFilters,
    handleApply: applyDraftFilters,
    isResetDisabled,
    isApplyDisabled,
  };
};
